// Language rules for the assistant language setting (assistants.language_configuration).
// Pure TypeScript: no React, no `@/` imports, no JSON imports, so plain Node can load it.
// Provider data (voice objects) comes in as arguments.
// The rules and the language table follow the language contract (npm run check:language).
// The [Language rules] prompt block is built by the voice server only; the dashboard does not show it.

export type LanguageCode = 'en' | 'ta' | 'hi' | 'te' | 'kn' | 'ml' | 'mr' | 'gu';
export type LanguageMode = 'single' | 'multilingual';
export type SwitchRule = 'ask' | 'follow';

export interface LanguageInfo {
  code: LanguageCode;
  name: string;
  /** Script name; 'Latin' for English. */
  script: string;
}

/** Display order everywhere. */
export const LANGUAGES: readonly LanguageInfo[] = [
  { code: 'en', name: 'English', script: 'Latin' },
  { code: 'ta', name: 'Tamil', script: 'Tamil' },
  { code: 'hi', name: 'Hindi', script: 'Devanagari' },
  { code: 'te', name: 'Telugu', script: 'Telugu' },
  { code: 'kn', name: 'Kannada', script: 'Kannada' },
  { code: 'ml', name: 'Malayalam', script: 'Malayalam' },
  { code: 'mr', name: 'Marathi', script: 'Devanagari' },
  { code: 'gu', name: 'Gujarati', script: 'Gujarati' },
];

export const LANGUAGE_CODES: readonly LanguageCode[] = LANGUAGES.map((l) => l.code);

/** The stored shape of assistants.language_configuration (snake_case, as in the database). */
export interface LanguageConfiguration {
  mode: LanguageMode;
  languages: LanguageCode[];
  primary: LanguageCode;
  switch_rule: SwitchRule;
}

export type ConflictSection = 'transcriber' | 'voice';

export interface LanguageConflict {
  section: ConflictSection;
  message: string;
}

/** A voice object from tts-providers.json. `languages` is absent on voices with no tag. */
export interface VoiceLike {
  id?: string;
  name?: string;
  languages?: string[];
}

export interface LanguageSetupInput {
  sttProvider?: string | null;
  sttModel?: string | null;
  ttsProvider?: string | null;
  /** A voice object from the config; undefined for a custom voice id. */
  voice?: VoiceLike | null;
  /** Display names; the provider id is used when missing. */
  sttProviderName?: string;
  ttsProviderName?: string;
}

const NAMES: Record<LanguageCode, string> = {
  en: 'English', ta: 'Tamil', hi: 'Hindi', te: 'Telugu',
  kn: 'Kannada', ml: 'Malayalam', mr: 'Marathi', gu: 'Gujarati',
};

export const languageName = (code: LanguageCode): string => NAMES[code];

const ALL_CODES: LanguageCode[] = [...LANGUAGE_CODES];

const isLanguageCode = (value: unknown): value is LanguageCode =>
  typeof value === 'string' && (LANGUAGE_CODES as readonly string[]).includes(value);

const joinWith = (items: string[], word: string): string =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} ${word} ${items[items.length - 1]}`;

// ---------------------------------------------------------------------------
// Checker
// ---------------------------------------------------------------------------

/**
 * Turns a raw stored value into a valid setting, or null when there is no setting or the value
 * breaks the rules. Never throws. Single mode comes back with switch_rule 'ask'.
 */
export function checkLanguageConfiguration(raw: unknown): LanguageConfiguration | null {
  try {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const value = raw as Record<string, unknown>;
    const mode = value.mode;
    if (mode !== 'single' && mode !== 'multilingual') return null;
    if (!Array.isArray(value.languages)) return null;
    const languages = value.languages as unknown[];
    if (!languages.every(isLanguageCode)) return null;
    const codes = languages as LanguageCode[];
    if (new Set(codes).size !== codes.length) return null;
    if (mode === 'single') {
      if (codes.length !== 1) return null;
      const primary = value.primary === undefined || value.primary === null ? codes[0] : value.primary;
      if (primary !== codes[0]) return null;
      return { mode, languages: codes, primary: codes[0], switch_rule: 'ask' };
    }
    if (codes.length < 2) return null;
    if (!isLanguageCode(value.primary) || !codes.includes(value.primary)) return null;
    return {
      mode,
      languages: codes,
      primary: value.primary,
      switch_rule: value.switch_rule === 'follow' ? 'follow' : 'ask',
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Provider rules
// ---------------------------------------------------------------------------

const STT_ALL_PROVIDERS = ['sarvam', 'sarvam-realtime', 'voxi-stt'];
const DEEPGRAM_MULTI_MODELS = ['flux-general-multi', 'nova-3'];

const STT_DISPLAY_NAMES: Record<string, string> = {
  deepgram: 'Deepgram',
  sarvam: 'Sarvam AI',
  'sarvam-realtime': 'Sarvam AI Realtime',
  'voxi-stt': 'Voxi STT',
};
const TTS_DISPLAY_NAMES: Record<string, string> = {
  'voxi-tts': 'Voxi TTS',
  cartesia: 'Cartesia AI',
  murf: 'Murf AI',
};

/** Languages the setting needs a provider, model or voice to speak (English is always spoken). */
function neededLanguages(setting: LanguageConfiguration | null): LanguageCode[] {
  if (!setting) return [];
  return setting.languages.filter((code) => code !== 'en');
}

const hasNonEnglish = (setting: LanguageConfiguration | null): boolean =>
  neededLanguages(setting).length > 0;

function sttProviderLanguages(provider: string): LanguageCode[] {
  if (STT_ALL_PROVIDERS.includes(provider)) return ALL_CODES;
  if (provider === 'deepgram') return ['en', 'hi'];
  return ['en'];
}

function sttModelLanguages(provider: string, model: string): LanguageCode[] {
  if (STT_ALL_PROVIDERS.includes(provider)) return ALL_CODES;
  if (provider === 'deepgram') return DEEPGRAM_MULTI_MODELS.includes(model) ? ['en', 'hi'] : ['en'];
  return ['en'];
}

function ttsProviderLanguages(provider: string): LanguageCode[] {
  return provider === 'voxi-tts' ? ALL_CODES : ['en', 'hi'];
}

const missingFrom = (setting: LanguageConfiguration | null, supported: LanguageCode[]): LanguageCode[] =>
  neededLanguages(setting).filter((code) => !supported.includes(code));

/** Can this STT provider be chosen for the setting? Null setting and English-only allow all. */
export function isSttProviderAllowed(setting: LanguageConfiguration | null, provider: string): boolean {
  return missingFrom(setting, sttProviderLanguages(provider)).length === 0;
}

/** Can this model of this STT provider be chosen for the setting? */
export function isSttModelAllowed(
  setting: LanguageConfiguration | null,
  provider: string,
  model: string,
): boolean {
  return missingFrom(setting, sttModelLanguages(provider, model)).length === 0;
}

/** Can this TTS provider be chosen for the setting? */
export function isTtsProviderAllowed(setting: LanguageConfiguration | null, provider: string): boolean {
  return missingFrom(setting, ttsProviderLanguages(provider)).length === 0;
}

/**
 * Can this voice be chosen for the setting? A voice with no `languages` array (or no voice at all,
 * as with a custom voice id) is never hidden. Provider-level checks are separate.
 */
export function isVoiceAllowed(setting: LanguageConfiguration | null, voice: VoiceLike | null | undefined): boolean {
  if (!setting || !hasNonEnglish(setting)) return true;
  if (!voice || !Array.isArray(voice.languages)) return true;
  const tags = voice.languages;
  return setting.languages.every((code) => tags.includes(code));
}

// ---------------------------------------------------------------------------
// Derived provider languages
// ---------------------------------------------------------------------------

/**
 * The STT `language` value the setting implies, or undefined when the dashboard should keep
 * showing its own language field.
 */
export function deriveSttLanguage(setting: LanguageConfiguration | null, provider: string): string | undefined {
  if (!setting || !hasNonEnglish(setting)) return undefined;
  switch (provider) {
    case 'voxi-stt': return 'multi';
    case 'sarvam': return 'unknown';
    case 'sarvam-realtime': return 'auto';
    case 'deepgram': return 'multi';
    default: return undefined;
  }
}

const MURF_LOCALES: Partial<Record<LanguageCode, string>> = { en: 'en-IN', hi: 'hi-IN', ta: 'ta-IN' };

/** The TTS `language` value the setting implies, or undefined to keep the dashboard's own field. */
export function deriveTtsLanguage(setting: LanguageConfiguration | null, provider: string): string | undefined {
  if (!setting || !hasNonEnglish(setting)) return undefined;
  if (provider === 'cartesia') return 'multilingual';
  if (provider === 'murf') return MURF_LOCALES[setting.primary];
  return undefined;
}

// ---------------------------------------------------------------------------
// Conflicts
// ---------------------------------------------------------------------------

const namesOf = (codes: LanguageCode[]): string => joinWith(codes.map((c) => NAMES[c]), 'and');

/** Conflicts between the setting and the chosen providers. Empty list = it fits. */
export function validateLanguageSetup(
  setting: LanguageConfiguration | null,
  input: LanguageSetupInput,
): LanguageConflict[] {
  const conflicts: LanguageConflict[] = [];
  if (!setting || !hasNonEnglish(setting)) return conflicts;

  const { sttProvider, sttModel, ttsProvider, voice } = input;

  if (sttProvider) {
    const label = input.sttProviderName || STT_DISPLAY_NAMES[sttProvider] || sttProvider;
    const missing = missingFrom(setting, sttProviderLanguages(sttProvider));
    if (missing.length > 0) {
      const options = Object.keys(STT_DISPLAY_NAMES)
        .filter((id) => isSttProviderAllowed(setting, id))
        .map((id) => STT_DISPLAY_NAMES[id]);
      conflicts.push({
        section: 'transcriber',
        message: `Transcriber: ${label} does not support ${namesOf(missing)}. Choose ${joinWith(options, 'or')}.`,
      });
    } else if (sttModel && !isSttModelAllowed(setting, sttProvider, sttModel)) {
      const missingModel = missingFrom(setting, sttModelLanguages(sttProvider, sttModel));
      const models = sttProvider === 'deepgram' ? DEEPGRAM_MULTI_MODELS : [];
      const hint = models.length > 0 ? ` Choose ${joinWith(models, 'or')}.` : '';
      conflicts.push({
        section: 'transcriber',
        message: `Transcriber: ${label} model ${sttModel} does not support ${namesOf(missingModel)}.${hint}`,
      });
    }
  }

  if (ttsProvider) {
    const label = input.ttsProviderName || TTS_DISPLAY_NAMES[ttsProvider] || ttsProvider;
    const missing = missingFrom(setting, ttsProviderLanguages(ttsProvider));
    if (missing.length > 0) {
      conflicts.push({
        section: 'voice',
        message: `Voice: ${label} does not support ${namesOf(missing)}. Choose ${TTS_DISPLAY_NAMES['voxi-tts']}.`,
      });
    } else if (voice && !isVoiceAllowed(setting, voice)) {
      const tags = voice.languages as string[];
      const missingVoice = neededLanguages(setting).filter((code) => !tags.includes(code));
      const who = voice.name ? `${label} voice ${voice.name}` : `${label} voice`;
      conflicts.push({
        section: 'voice',
        message: `Voice: ${who} does not speak ${namesOf(missingVoice)}. Choose a voice that speaks ${namesOf(neededLanguages(setting))}.`,
      });
    }
  }

  return conflicts;
}

// ---------------------------------------------------------------------------
// First message helpers
// ---------------------------------------------------------------------------

// `{{name}}` is the form the dashboard uses; `{name}` is accepted too.
const PLACEHOLDER = /\{\{[^{}]*\}\}|\{[^{}]*\}/g;

// Unicode blocks, written as escapes so an editor cannot change them unseen.
const SCRIPT_RANGES: Record<LanguageCode, RegExp> = {
  en: /[A-Za-z]/,
  ta: /[஀-௿]/,
  hi: /[ऀ-ॿ]/,
  te: /[ఀ-౿]/,
  kn: /[ಀ-೿]/,
  ml: /[ഀ-ൿ]/,
  mr: /[ऀ-ॿ]/,
  gu: /[઀-૿]/,
};

/** The placeholder tokens of a text, in order. */
export function placeholdersOf(text: string): string[] {
  return (text || '').match(PLACEHOLDER) ?? [];
}

/**
 * True when the text, with placeholders removed, has a character of the primary language's script.
 * A blank text counts as true (nothing to warn about).
 */
export function greetingHasPrimaryScript(text: string, primaryCode: LanguageCode): boolean {
  const stripped = (text || '').replace(PLACEHOLDER, '');
  if (stripped.trim() === '') return true;
  return SCRIPT_RANGES[primaryCode].test(stripped);
}
